import { create } from "zustand";
import { RoleTypes } from "../../types";
import SecureStorage from "../utils/secureStorage";
import { TokenUtils } from "../utils/tokenUtils";
import { config } from "../../config";
import Logger from "../utils/logger";
import RoleValidator from "../utils/roleValidator";

interface IUserProfile {
  id: number;
  email: string;
  last_login: string;
  role: number;
  social_type: string | null;
  social_id: string | null;
  first_name: string;
  last_name: string;
  phone: string;
}

interface User {
  token: string | null;
  refresh: string | null;
  role: RoleTypes;
  user_id: string | null;
  info: IUserProfile | null;
  profile: any;
}

interface AuthState {
  user: User;
  isAuthenticated: boolean;
  isInitialized: boolean; // Add initialization flag
  lastActivity: number;
  sessionTimeoutId: NodeJS.Timeout | null;
  afterLogin: (
    token: string,
    refresh: string,
    role: RoleTypes,
    info: IUserProfile,
    profile: any
  ) => void;
  logOut: () => Promise<void>;
  initializeAuth: () => void;
  clearAuth: () => void;
  updateActivity: () => void;
  checkSessionTimeout: () => void;
  getSecureRole: () => RoleTypes;
}

export const useAuth = create<AuthState>()((set, get) => ({
  user: {
    user_id: null,
    token: null,
    refresh: null,
    role: "Default_Role",
    info: null,
    profile: null,
  },
  isAuthenticated: false,
  isInitialized: false, // Initialize as false until auth check is complete
  lastActivity: Date.now(),
  sessionTimeoutId: null,

  updateActivity: () => {
    set({ lastActivity: Date.now() });
    get().checkSessionTimeout();
  },

  // Check if session has timed out
  checkSessionTimeout: () => {
    const state = get();
    if (!state.isAuthenticated) return;

    const timeSinceLastActivity = Date.now() - state.lastActivity;

    if (timeSinceLastActivity >= config.sessionTimeout) {
      Logger.security("Session timeout - logging out user");
      state.logOut();
      return;
    }

    // Clear existing timeout
    if (state.sessionTimeoutId) {
      clearTimeout(state.sessionTimeoutId);
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      Logger.security("Session timeout reached");
      get().logOut();
    }, config.sessionTimeout - timeSinceLastActivity);

    set({ sessionTimeoutId: timeoutId });
  },

  // Initialize auth state from secure storage on app start
  initializeAuth: () => {
    const token = SecureStorage.getToken();
    const refreshToken = SecureStorage.getRefreshToken();
    const userData = SecureStorage.getUserData();

    if (token && refreshToken && !TokenUtils.isTokenExpired(token)) {
      const decodedToken = TokenUtils.decodeToken(token);

      if (decodedToken) {
        set({
          user: {
            user_id: String(decodedToken.user_id),
            token,
            refresh: refreshToken,
            role: userData?.role || "Default_Role",
            info: userData?.info || null,
            profile: userData?.profile || null,
          },
          isAuthenticated: true,
          isInitialized: true,
          lastActivity: Date.now(),
        });

        Logger.security("Auth restored from storage", { prefix: "AUTH" });

        // Start session timeout monitoring
        get().checkSessionTimeout();
      } else {
        // Invalid token, clear everything
        get().clearAuth();
        set({ isInitialized: true });
      }
    } else {
      // No valid session, clear everything and mark as initialized
      get().clearAuth();
      set({ isInitialized: true });
    }
  },

  afterLogin: (
    token: string,
    refresh: string,
    role: RoleTypes,
    info: IUserProfile,
    profile: any
  ) => {
    // Decode token safely with proper typing
    const decodedToken = TokenUtils.decodeToken(token);

    if (!decodedToken) {
      Logger.error("Invalid token received during login");
      return;
    }

    // Store tokens securely
    SecureStorage.setToken(token);
    SecureStorage.setRefreshToken(refresh);

    // Store user data (including role from login response since JWT doesn't contain it)
    const userData = { role, info, profile };
    SecureStorage.setUserData(userData);

    set({
      user: {
        user_id: String(decodedToken.user_id),
        token,
        refresh,
        role, // Use role from login response, not from token
        info,
        profile,
      },
      isAuthenticated: true,
      lastActivity: Date.now(),
    });

    Logger.security(`User logged in successfully with role: ${role}`, {
      prefix: "AUTH",
    });

    // Start session timeout monitoring
    get().checkSessionTimeout();
  },

  clearAuth: () => {
    // Clear session timeout
    const state = get();
    if (state.sessionTimeoutId) {
      clearTimeout(state.sessionTimeoutId);
    }

    SecureStorage.clearAll();
    set({
      user: {
        token: null,
        refresh: null,
        role: "Default_Role",
        user_id: null,
        info: null,
        profile: null,
      },
      isAuthenticated: false,
      // Don't reset isInitialized here - keep it true once initialized
      lastActivity: Date.now(),
      sessionTimeoutId: null,
    });

    Logger.security("Auth cleared", { prefix: "AUTH" });
  },

  logOut: async () => {
    // Clear all sensitive data first
    get().clearAuth();

    // Clear role validator cache
    RoleValidator.clearRoleCache();

    // Then redirect to login
    window.location.href = "/login";
  },

  // Get role securely with JWT token validation but fallback to stored role
  getSecureRole: (): RoleTypes => {
    const state = get();
    const token = SecureStorage.getToken();

    // First check if we have a valid token
    if (!token || TokenUtils.isTokenExpired(token)) {
      Logger.security("Invalid or expired token during role check");
      return "Default_Role";
    }

    // Since JWT doesn't contain role, validate token and return stored role
    const userData = SecureStorage.getUserData();
    const storedRole = userData?.role || state.user.role;

    if (storedRole && storedRole !== "Default_Role") {
      // Token is valid and we have a stored role - this is secure
      return storedRole;
    }

    return "Default_Role";
  },
}));
